import React, { useState, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { openDashboardDialog } from '@actions/dashboard-actions';
import { dotmapsBlue, textColor } from '@constants/colors';
import * as dialog from '@constants/dialogs';
import { listItemStyles } from '@constants/mui-theme';
import { Divider, FormControlLabel, Icon, IconButton, Radio, RadioGroup } from '@mui';
import FilterCheckbox from '@shared/filter-checkbox';
import { setMapFilters } from '@actions/map-actions';
import { getCurrentMapFilters } from '@selectors/map-selector';
import {
  decodeDateFilters,
  generateDateFilters,
  selectedDateFilterValue
} from '@utils/date-utils';
import { formatDate } from '@utils/shared-utils';
import './filter.scss';

const DateFilter = ({ config }) => {
  const dispatch = useDispatch();
  const filters = useSelector(getCurrentMapFilters);
  const { start, end, contain, undated, undatedOnly } = decodeDateFilters(filters);
  const selected = selectedDateFilterValue(start, end);

  const [open, setOpen] = useState(false);

  const toggleOpen = useCallback(() => setOpen(prev => !prev), []);

  const filterChange = useCallback((event, value) => {
    let newEnd = null;
    let newStart = null;
    switch (value) {
    case 'today': {
      newStart = moment();
      newEnd = moment();
      break;
    }
    case 'tomorrow': {
      newStart = moment().add(1, 'days');
      newEnd = moment().add(1, 'days');
      break;
    }
    case 'next7': {
      newStart = moment();
      newEnd = moment().add(7, 'days');
      break;
    }
    case 'next30': {
      newStart = moment();
      newEnd = moment().add(30, 'days');
      break;
    }
    case 'next90': {
      newStart = moment();
      newEnd = moment().add(90, 'days');
      break;
    }
    case 'nextYear': {
      newStart = moment();
      newEnd = moment().add(365, 'days');
      break;
    }
    case 'next3Years': {
      newStart = moment();
      newEnd = moment().add(1095, 'days');
      break;
    }
    case 'custom': {
      // Use last assigned start/end dates:
      newStart = start;
      newEnd = end;
      break;
    }
    default:
      break;
    }
    dispatch(setMapFilters(generateDateFilters({ start: newStart, end: newEnd, contain })));
  }, [contain, dispatch, end, start]);

  const toggleContain = useCallback(() => {
    dispatch(setMapFilters(generateDateFilters({ start, end, contain: !contain, undated, undatedOnly })));
  }, [contain, dispatch, end, start, undated, undatedOnly]);

  const toggleUndated = useCallback(() => {
    const newUndatedOnly = undated ? false : undatedOnly;
    dispatch(setMapFilters(generateDateFilters({ start, end, contain, undated: !undated, undatedOnly: newUndatedOnly })));
  }, [contain, dispatch, end, start, undated, undatedOnly]);

  const toggleUndatedOnly = useCallback(() => {
    dispatch(setMapFilters(generateDateFilters({ start, end, contain, undated, undatedOnly: !undatedOnly })));
  }, [contain, dispatch, end, start, undated, undatedOnly]);

  const openCustomDateDialog = useCallback(() => {
    dispatch(openDashboardDialog(dialog.DATE_RANGE));
  }, [dispatch]);

  const getRadio = (value, label, button) => (
    <FormControlLabel
      value={value}
      control={<Radio color="primary" style={{ padding: '0.5rem', paddingRight: 0, marginRight: 0 }} />}
      label={(
        <div styleName="radio">
          {button}
          <div styleName="label">
            {label}
          </div>
        </div>
      )}
      style={{ width: '7.5rem', whiteSpace: 'nowrap', marginLeft: 0 }}
    />
  );

  const renderLabel = () => (
    <div styleName="bold">
      {start && end && (
        <div>
          {formatDate(start)} - {formatDate(end)}
        </div>
      )}
      {(!start || !end) && 'Any time'}
    </div>
  );

  const contentId = `filter-content-${config.type || 'date'}`;

  const renderAction = () => (
    <IconButton
      aria-controls={contentId}
      aria-expanded={open}
      aria-label={open ? 'collapse' : 'expand'}
      onClick={toggleOpen}
      style={listItemStyles.filterButtonStyle}
    >
      <Icon color={textColor} role="img">
        {open ? 'expand_less' : 'expand_more'}
      </Icon>
    </IconButton>
  );

  const renderIcon = () => (
    <div styleName="icon no-toggle">
      <Icon
        aria-label="Date range"
        color={dotmapsBlue}
        role="img"
        style={{ fontSize: '1.5rem' }}
      >
        {config.icon}
      </Icon>
    </div>
  );

  const customEditButton = (
    <div
      styleName="button custom-button"
      onClick={openCustomDateDialog}
      onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && openCustomDateDialog()}
      role="button"
      tabIndex={0}
      aria-label="Edit custom date range"
    >
      EDIT
    </div>
  );

  const renderContent = () => (
    <div styleName="content-list-container radio-button-group-container">
      <RadioGroup
        aria-label="Date range filter"
        name="date"
        defaultValue={selected}
        value={selected}
        onChange={filterChange}
        row
        style={{ flexWrap: 'wrap' }}
      >
        {getRadio('today', 'Today')}
        {getRadio('tomorrow', 'Tomorrow')}
        {getRadio('next7', 'Next 7 days')}
        {getRadio('next30', 'Next 30 days')}
        {getRadio('next90', 'Next 90 days')}
        {getRadio('nextYear', '1 year')}
        {getRadio('next3Years', '3 years')}
        {getRadio('custom', 'Custom range', customEditButton)}
      </RadioGroup>
      <div styleName="divider">
        <Divider />
      </div>
      <div styleName="list-item">
        <div styleName="checkbox">
          <FilterCheckbox checked={contain} onChange={toggleContain} name="Show fully contained records only" />
        </div>
        <div styleName="label">
          Show fully contained records only
        </div>
      </div>
      <div styleName="list-item">
        <div styleName="checkbox">
          <FilterCheckbox checked={undated} onChange={toggleUndated} name="Show undated records" />
        </div>
        <div styleName="label">
            Show undated records
        </div>
      </div>
      <div styleName="list-item indent">
        <div styleName="checkbox">
          <FilterCheckbox disabled={!undated} checked={undatedOnly} onChange={toggleUndatedOnly} name="Show undated only" />
        </div>
        <div styleName="label">
            Show undated only
        </div>
      </div>
    </div>
  );

  const content = renderContent();
  const hasContent = content.props.children && content.props.children.length > 0;

  return (
    <div styleName="filter-container">
      <div styleName="row">
        <div styleName="toggle">
          {/* No toggle for date filter */}
        </div>
        {renderIcon()}
        <div styleName="label">
          {renderLabel()}
        </div>
        {hasContent && (
          <div styleName="action">
            {renderAction()}
          </div>
        )}
      </div>
      {open && (
        <div id={contentId} styleName="content">
          {content}
        </div>
      )}
    </div>
  );
};

DateFilter.propTypes = {
  config: PropTypes.object
};

export default memo(DateFilter);
